<template>
  <b-card>
    <layout-loading v-if="itemLoading" />
    <b-form v-if="render">
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="article-title"
          >
            <b-form-input
              id="article-title"
              v-model="form.title"
              :state="validateState('title')"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="article-slug"
          >
            <b-form-input
              id="article-slug"
              v-model="form.slug"
              :state="validateState('slug')"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.language')"
            label-for="article-lang"
          >
            <language-selector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.status')"
            label-for="article-status"
            :state="validateState('status')"
          >
            <b-form-select v-model="form.status">
              <b-form-select-option
                v-for="(status, index) in statusOptions"
                :key="index"
                :value="status"
              >
                {{ $t(`articles.${status}`) }}
              </b-form-select-option>
            </b-form-select>
            <b-form-invalid
              id="input-status-feedback"
              :label="$t('general.status')"
              :validator="$v.form.status"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.studyTime')"
            label-for="article-study-time"
          >
            <b-form-input
              id="article-study-time"
              v-model.number="form.studyTime"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.showOnMenu')"
            label-for="article-showOnMenu"
          >
            <b-form-checkbox
              id="article-showOnMenu"
              v-model="form.showOnMenu"
              class="mt-1"
              name="article-showOnMenu"
              switch
              inline
            />
            <b-form-invalid
              id="input-showOnMenu-feedback"
              :label="$t('general.showOnMenu')"
              :validator="$v.form.showOnMenu"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.waterMark')"
            label-for="article-watermark"
          >
            <b-form-checkbox
              id="article-watermark"
              v-model="form.watermark"
              class="mt-1"
              name="article-waterMark"
              switch
              inline
            />
            <b-form-invalid
              id="input-watermark-feedback"
              :label="$t('general.waterMark')"
              :validator="$v.form.watermark"
            />
          </b-form-group>
        </b-col>

        <b-col md="2">
          <b-form-group
            :label="$t('general.tableOfContentShow')"
            label-for="article-tableOfContentShow"
          >
            <b-form-checkbox
              id="article-tableOfContentShow"
              v-model="form.showTableOfContent"
              class="mt-1"
              name="article-tableOfContentShow"
              switch
              inline
            />
            <b-form-invalid
              id="input-tableOfContentShow-feedback"
              :label="$t('general.tableOfContentShow')"
              :validator="$v.form.showTableOfContent"
            />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group
            :label="$t('general.category')"
            label-for="article-category"
            :state="validateState('category')"
          >
            <v-select-custom
              id="article-category"
              v-model="form.category"
              :options="rootCategoryOptions"
            />
            <b-form-invalid
              id="input-category-feedback"
              :label="$t('general.category')"
              :validator="$v.form.category"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.tags')"
            label-for="article-tag"
            :state="validateState('tags')"
          >
            <v-select-custom
              id="article-tags"
              v-model="form.tags"
              :custom-props="{ multiple: true, taggable: true }"
              :options="tags"
              @search="fetchTags(form.lang, $event)"
              @option:created="addNewTag(form.lang, $event)"
            />
            <b-form-invalid
              id="input-tags-feedback"
              :label="$t('general.tags')"
              :validator="$v.form.tags"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.approver')"
            label-for="article-approver"
          >
            <v-select
              id="article-approver"
              v-model="form.approver"
              label="firstName"
              dir="rtl"
              :options="approverOptions"
              @search="fetchApprovers(form.lang, $event)"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.managerImageFileId"
                  :title="option.firstName + option.lastName"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.managerImageFileId"
                  :title="option.firstName + option.lastName"
                />
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.metaTitle')"
            label-for="article-category-metaTitle"
          >
            <b-form-input
              id="article-category-metaTitle"
              v-model="form.metaTitle"
              :state="validateState('metaTitle')"
            />
            <b-form-invalid
              id="input-metaTitle-feedback"
              :label="$t('general.metaTitle')"
              :validator="$v.form.metaTitle"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.writer')"
            label-for="article-writer"
          >
            <v-select-custom
              id="article-writer"
              v-model="form.writer"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'writer' }, form.lang)"
              @search="fetchUsersByRole({role: 'writer', 'search' : $event}, form.lang)"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.editor')"
            label-for="article-editor"
          >
            <v-select-custom
              id="article-editor"
              v-model="form.editor"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'editor' }, form.lang)"
              @search="fetchUsersByRole({role: 'editor', 'search' : $event}, form.lang)"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.relatedArticles')"
            label-for="article-related-articles"
            :state="validateState('relatedArticles')"
          >
            <v-select
              id="article-related-articles"
              v-model="form.relatedArticles"
              label="title"
              dir="rtl"
              multiple
              :options="articleOptions"
              @search="fetchArticles(form.lang, $event, 'title')"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title"
                />
              </template>
            </v-select>
            <b-form-invalid
              id="input-relatedArticles-feedback"
              :label="$t('articles.relatedArticles')"
              :validator="$v.form.relatedArticles"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.relatedServices')"
            label-for="article-related-services"
          >
            <v-select
              id="article-related-services"
              v-model="form.relatedServices"
              dir="rtl"
              label="title"
              multiple
              :options="serviceOptions"
              @search="fetchServices(form.lang, $event, 'title')"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.serviceThumbnailFileId"
                  :title="option.title"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.serviceThumbnailFileId"
                  :title="option.title"
                />
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.relatedQuestions')"
            label-for="article-related-questions"
          >
            <v-select
              id="article-related-questions"
              v-model="form.relatedQuestions"
              dir="rtl"
              label="title"
              multiple
              :options="questionOptions"
              @search="fetchQuestions(form.lang, $event, 'title')"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.relatedArticleCategories')"
            label-for="article-related-categories"
          >
            <v-select
              id="article-related-categories"
              v-model="form.relatedCategories"
              dir="rtl"
              label="title"
              multiple
              :options="categoryOptions"
              @search="fetchCategories(form.lang, $event, 'title')"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="article-keywords"
          >
            <b-form-input
              id="article-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.canonicalUrl')"
            label-for="article-canonicalUrl"
            class="mb-2"
          >
            <b-form-input
              id="article-canonicalUrl"
              v-model="form.canonicalUrl"
              :state="validateState('canonicalUrl')"
            />
            <b-form-invalid
              id="input-canonicalUrl-validator"
              :label="$t('general.canonicalUrl')"
              :validator="$v.form.canonicalUrl"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col>
          <b-button
            variant="outline-primary"
            @click="addSourceItem"
          >
            {{ $t("general.addSourceItem") }}
          </b-button>
        </b-col>
      </b-row>
      <b-row
        v-for="(source, index) in form.source"
        :key="index"
        class="mt-1 bg-booking p-1"
      >
        <b-col md="3">
          <b-form-group
            :label="$t('general.sourceName')"
            label-for="article-source-name"
          >
            <b-form-input
              id="article-source-name"
              v-model="source.sourceName"
            />
          </b-form-group>
        </b-col>
        <b-col md="7">
          <b-form-group
            :label="$t('general.sourceLink')"
            label-for="article-source-link"
          >
            <b-form-input
              id="article-source-link"
              v-model="source.sourceLink"
            />
          </b-form-group>
        </b-col>
        <b-col class="mt-2">
          <b-button
            variant="outline-success"
            @click="removeSourceItem(index)"
          >
            {{ $t("general.removeSourceItem") }}
          </b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="article-description"
          >
            <b-form-textarea
              id="article-description"
              v-model="form.description"
              :state="validateState('description')"
              rows="6"
              aria-describedby="input-description-feedback"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="h2Tags && h2Tags.length != 0 && form.showTableOfContent"
          class="mb-2"
          cols="12"
        >
          <h3 class="text-center">
            {{ $t('articles.h2Tags') }}
          </h3>
          <hr>
          <table class="table table-bordered table-hover">
            <thead>
              <tr>
                <th style="width: 60px;">
                  {{ $t('general.row') }}
                </th>
                <th style="width: 80%">
                  {{ $t('articles.h2Text') }}
                </th>
                <th>{{ $t('articles.h2Id') }}</th>
                <!-- <th>{{ $t('articles.h2Op') }}</th> -->
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item,index) in h2Tags"
                :key="index"
              >
                <td>{{ ++index }}</td>
                <td>
                  <p v-if="!item.editable">
                    {{ item.text }}
                  </p>
                  <b-form-group
                    v-if="item.editable"
                    :label-for="item.id"
                  >
                    <b-form-input
                      :id="item.id"
                      v-model="newH2Text"
                    />
                  </b-form-group>
                </td>
                <td>{{ item.id }}</td>
                <!-- <td>
                  <feather-icon
                    icon="EditIcon"
                    @click="showEditable(index)"
                    size="18"
                    opacity=".6"
                    hover-opacity=".9"
                    class="hover-scale-up mr-1"
                  />
                </td> -->
              </tr>
            </tbody>
          </table>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <CampaignSettings
            :value="form.campaignSettings || {}"
            :lang="form.lang || 'fa'"
            @input="$set(form, 'campaignSettings', $event)"
          />
          <ArticleDoctorEditor
            :mode="form.doctorBlockMode || 'auto'"
            :ids="form.recommendedDoctorIds || []"
            :enabled="form.doctorBlockEnabled === true"
            :placement="form.doctorBlockPlacement || 'middle'"
            :tags="form.tags || []"
            :lang="form.lang || 'fa'"
            @change="setDoctorBlock"
          />
          <b-form-group
            label-for="article-content"
            class="mb-2"
          >
            <CkeditorFull
              id="article-content"
              ref="editor"
              v-model="form.content"
              :label="$t('general.content')"
              :state="validateState('content')"
            />
            <b-form-invalid
              id="input-content-feedback"
              :label="$t('general.content')"
              :validator="$v.form.content"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="articleThumbnail"
            v-model="form.articleThumbnail"
            :label="$t('general.thumbnail') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.select')"
            :state="validateState('articleThumbnail')"
            :alt.sync="form.articleThumbnailAlt"
            :alt-validator="$v.form.articleThumbnailAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-article-thumbnail-feedback"
                :label="$t('general.thumbnail')"
                :validator="$v.form.articleThumbnail"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="articleCoverImage"
            v-model="form.articleCoverImage"
            :label="$t('general.cover') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.select')"
            :state="validateState('articleCoverImage')"
            :alt.sync="form.articleCoverImageAlt"
            :alt-validator="$v.form.articleCoverImageAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-article-cover-feedback"
                :label="$t('general.cover')"
                :validator="$v.form.articleCoverImage"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BButton,
  BCard,
  BCol,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BFormTextarea,
  BRow,
} from 'bootstrap-vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import CkeditorFull from '@/components/CkeditorFull/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import VSelectOption from '@/components/VSelectCustom/VSelectOption.vue'
import languageSelector from '@/components/language-selector.vue'
import useBasicToast from '@/composable/useBasicToast'
import vSelect from 'vue-select'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import useArticlesList from './useArticlesList'
import CampaignSettings from '../contentCampaign/CampaignSettings.vue'
import ArticleDoctorEditor from './components/ArticleDoctorEditor.vue'

export default {
  name: 'ArticleEdit',
  components: {
    ArticleDoctorEditor,
    CampaignSettings,
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormSelect,
    BFormSelectOption,
    BFormTextarea,
    BRow,
    CkeditorFull,
    LayoutLoading,
    VSelectCustom,
    VSelectOption,
    languageSelector,
    vSelect,
  },
  directives: {
    Ripple,
  },
  validations() {
    return {
      form: {
        watermark: {},
        showOnMenu: {},
        showTableOfContent: {},
        studyTime: {},
        title: { required },
        metaTitle: { required },
        slug: { required, isUnique: () => this.isUniqueSlug },
        tags: { required },
        category: { required },
        keywords: { minLength: minLength(3) },
        description: { required, minLength: minLength(160), maxLength: maxLength(320) },
        articleThumbnail: { required },
        articleCoverImage: { required },
        articleThumbnailAlt: { required, minLength: minLength(3) },
        articleCoverImageAlt: { required, minLength: minLength(3) },
        relatedArticles: {},
        status: { required },
        canonicalUrl: {},
        content: { required, minLength: minLength(10) },
      },
    }
  },

  setup() {
    const toast = useBasicToast()
    const {
      addItem,
      addNewTag,
      approverOptions,
      articleOptions,
      categoryOptions,
      checkSlugIsUnique,
      convertResource,
      createTagIdArray,
      errorManager,
      fetchApprovers,
      fetchArticles,
      fetchCategories,
      fetchItem,
      fetchQuestions,
      fetchServices,
      fetchTags,
      fetchUsers,
      fetchUsersByRole,
      userOptions,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      newTagIds,
      questionOptions,
      rootCategoryOptions,
      serviceOptions,
      statusOptions,
      tags,
      updateItem,
    } = useArticlesList()

    return {
      addItem,
      addNewTag,
      approverOptions,
      articleOptions,
      categoryOptions,
      checkSlugIsUnique,
      convertResource,
      createTagIdArray,
      errorManager,
      fetchApprovers,
      fetchArticles,
      fetchCategories,
      fetchItem,
      fetchQuestions,
      fetchServices,
      fetchTags,
      fetchUsers,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      newTagIds,
      questionOptions,
      rootCategoryOptions,
      serviceOptions,
      statusOptions,
      tags,
      toast,
      updateItem,
      userOptions,
    }
  },
  data() {
    return {
      sources: [],
      searchT: '',
      h2Tags: [],
      newH2Text: '',
      relatedArticlesByLang: {},
    }
  },
  computed: {
    itemId() {
      return this.$router.currentRoute.params.id
    },
    render() {
      return this.item ? this.item && Object.keys(this.item).length : true
    },
  },
  watch: {
    'form.slug': function () {
      this.checkSlugIsUnique(this.form.lang, this.form.slug)
    },
  },
  created() {
    this.fetchItem(this.itemId).then(() => {
      // load per-language cache (so AR/FA don't mix after reload)
      try {
        const raw = localStorage.getItem(`article.relatedArticlesByLang.${this.itemId}`)
        const parsed = raw ? JSON.parse(raw) : null
        if (parsed && typeof parsed === 'object') this.relatedArticlesByLang = parsed
      } catch (e) {
        /* ignore */
      }
      this.restoreRelatedArticlesForLang(this.form.lang)
      this.fetchTags(this.form.lang)
      this.fetchApprovers()
      this.fetchArticles(this.form.lang, this.searchT, 'title')
      this.fetchCategories(this.form.lang, '', '', 'null')
      this.fetchServices(this.form.lang)
      this.fetchQuestions(this.form.lang)
    })
  },
  mounted() {
    this.fetchArticles(this.form.lang, this.searchT, '')
    this.fetchCategories(this.form.lang, ' ', 'title')

    setTimeout(() => {
      this.getH2Tag()
    }, 3000)
  },
  methods: {
    validationFieldLabels() {
      const labels = {
        title: this.$t('general.title'),
        metaTitle: this.$t('general.metaTitle'),
        slug: this.$t('general.slug'),
        tags: this.$t('general.tags'),
        category: this.$t('general.category'),
        description: this.$t('general.description'),
        articleThumbnail: this.$t('general.thumbnail'),
        articleCoverImage: this.$t('general.cover'),
        articleThumbnailAlt: this.$t('general.alt'),
        articleCoverImageAlt: this.$t('general.alt'),
        status: this.$t('articles.status'),
        content: this.$t('general.content'),
      }
      return Object.keys(labels).filter(field => this.$v.form[field] && this.$v.form[field].$invalid).map(field => labels[field])
    },
    setDoctorBlock(value) { Object.keys(value).forEach(key => this.$set(this.form, key, value[key])) },
    addSourceItem() {
      if (this.form.source.length === 0) {
        this.form.source.push({ sourceName: '', sourceLink: '' })
      } else if (this.form.source[this.form.source.length - 1].clinic !== '') {
        this.form.source.push({ sourceName: '', sourceLink: '' })
      }
    },
    removeSourceItem(index) {
      this.form.source.splice(index, 1)
    },
    cacheRelatedArticlesForLang(lang) {
      if (!lang) return
      const ids = (this.form.relatedArticles || []).map(a => a && a._id).filter(Boolean)
      this.$set(this.relatedArticlesByLang, lang, ids)
      try {
        localStorage.setItem(
          `article.relatedArticlesByLang.${this.itemId}`,
          JSON.stringify(this.relatedArticlesByLang || {}),
        )
      } catch (e) {
        /* ignore */
      }
    },
    restoreRelatedArticlesForLang(lang) {
      const savedIds = this.relatedArticlesByLang && this.relatedArticlesByLang[lang]
      if (!savedIds) {
        // first time visiting this lang: cache what we currently have
        this.cacheRelatedArticlesForLang(lang)
        return
      }
      const currentRows = this.form.relatedArticles || []
      const byId = currentRows.reduce((acc, row) => {
        if (row && row._id) acc[row._id] = row
        return acc
      }, {})
      this.form.relatedArticles = savedIds.map(id => byId[id] || { _id: id, title: '' })
    },
    mergedRelatedArticleIdsForSubmit() {
      // include current tab
      this.cacheRelatedArticlesForLang(this.form.lang)
      const merged = new Set()
      Object.values(this.relatedArticlesByLang || {}).forEach(arr => {
        (arr || []).forEach(id => merged.add(id))
      })
      return [...merged]
    },
    changeSelectedLanguage(selectedLanguage) {
      // cache current selections before switching tab
      this.cacheRelatedArticlesForLang(this.form.lang)
      this.form = this.convertResource(this.item, selectedLanguage)
      // restore language-specific related articles (so AR doesn't show in FA)
      this.restoreRelatedArticlesForLang(selectedLanguage)
      this.fetchCategories(selectedLanguage, '', '', 'null')
      this.fetchArticles(selectedLanguage)
      this.fetchTags(selectedLanguage)
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    showEditable(index) {
      this.h2Tags[index - 1].editable = !this.h2Tags[index - 1].editable
      this.newH2Text = this.h2Tags[index - 1].text
    },
    getH2Tag() {
      const el = document.getElementById('article-content')
      if (el) {
        const allChilds = el.children
        const childArray = [...allChilds]
        for (let i = 0; i < childArray.length; i += 1) {
          if (childArray[i].localName === 'h2') {
            const data = {
              text: childArray[i].innerText,
              editable: false,
              id: `id-h2-${i}`,
            }
            childArray[i].setAttribute('id', `id-h2-${i}`)
            this.h2Tags.push(data)
          }
        }
      }
    },
    submitForm() {
      this.$refs.editor.sendValToForm()
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        const fields = this.validationFieldLabels()
        this.toast({
          title: fields.length ? `${this.$t('validator.validationError')}: ${fields.join('، ')}` : this.$t('validator.validationError'),
          type: 'danger',
        })
        setTimeout(() => {
          const invalid = document.getElementsByClassName('is-invalid')[0]
          if (invalid) invalid.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 500)
        return
      }
      const uploadList = ['articleCoverImage', 'articleThumbnail']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData(form) {
      const data = {
        title: form.title,
        metaTitle: form.metaTitle,
        slug: form.slug,
        studyTime: form.studyTime,
        showOnMenu: form.showOnMenu,
        showTableOfContent: form.showTableOfContent,
        watermark: this.form.watermark != null ? this.form.watermark : false,
        keywords: form.keywords,
        description: form.description,
        tagIds: this.createTagIdArray(this.form.tags, this.newTagIds),
        approverId: form.approver ? form.approver._id : null,
        writerId: form.writer ? form.writer._id : null,
        editorId: form.editor ? form.editor._id : null,
        // keep relations from other languages too, but show per-lang in UI
        relatedArticleIds: this.mergedRelatedArticleIdsForSubmit(),
        relatedQuestionIds: form.relatedQuestions ? form.relatedQuestions.map(item => item._id) : [],
        relatedServiceIds: form.relatedServices ? form.relatedServices.map(item => item._id) : [],
        relatedCategoryIds: form.relatedCategories ? form.relatedCategories.map(item => item._id) : [],
        source: this.form.source ? this.form.source.map(item => ({ sourceName: item.sourceName, sourceLink: item.sourceLink })) : [],
        articleCoverImageFileId: form.articleCoverImage._id,
        articleThumbnailFileId: form.articleThumbnail._id,
        status: form.status,
        categoryId: form.category ? form.category._id : null,
        content: form.content,
        ...(form.campaignSettings ? { campaignSettings: form.campaignSettings } : {}),
        recommendedDoctorIds: form.recommendedDoctorIds || [],
        doctorBlockMode: form.doctorBlockMode || 'auto',
        doctorBlockEnabled: form.doctorBlockMode === 'manual',
        doctorBlockPlacement: form.doctorBlockPlacement || 'middle',
        canonicalUrl: form.canonicalUrl,
        type: 'article',
      }
      this.updateItem(this.itemId, data, this.form.lang)
    },
  },
}
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss"
</style>
